import { useApolloClient } from "@apollo/client";
import {
  Add as AddIcon,
  WarningAmber as WarningAmberIcon,
} from "@mui/icons-material";
import { Button, Stack, Typography } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import * as Sentry from "@sentry/react";
import moment from "moment";
import React from "react";
import { FileInput } from "../../../commons/inputs/FileInput.js";
import {
  PimCsvColumnMappingProcess,
  PimCsvColumnMappingProcessRef,
} from "../PimCsvColumnMappingProcess.js";
import {
  useAddPimImportMutation,
  usePimRequestImportationUploadUrlMutation,
} from "./AddPimImportationButton.generated.js";

export const AddPimImportationButton = ({
  supplierId,
  refetchQueries,
  appearance = "header",
}: {
  supplierId: string;
  refetchQueries?: string[];
  appearance?: "header" | "cta";
}) => {
  const client = useApolloClient();
  const { t } = useTranslate(["PimImportations", "Global"]);
  const [status, setStatus] = React.useState<"idle" | "loading" | "error">(
    "idle"
  );
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const pimMappingProcessRef = React.useRef<PimCsvColumnMappingProcessRef>();
  // Remember the last attempted files so "Upload again" can retry them as-is.
  const lastFilesRef = React.useRef<File[] | null>(null);

  const [pimRequestImportationUploadUrlMutation] =
    usePimRequestImportationUploadUrlMutation({
      client,
    });

  const [addPimImport] = useAddPimImportMutation({
    client,
    refetchQueries,
  });

  const uploadFiles = React.useCallback(
    async (filelist: File[]) => {
      lastFilesRef.current = filelist;
      setStatus("loading");

      const uploadFile = async (file: File) => {
        const result = await pimRequestImportationUploadUrlMutation({
          variables: {
            input: {
              supplierId,
              filename: file.name,
            },
          },
        });

        const uploadUrl = result.data!.pimRequestImportationUploadUrl.uploadUrl;
        const response = await fetch(uploadUrl, {
          method: "PUT",
          body: file,
        });
        if (!response.ok) {
          throw new Error(`Upload failed with status ${response.status}`);
        }

        return {
          filename: file.name,
          uploadUrl,
        };
      };

      try {
        const files = Array.from(filelist);
        const zipFiles = files.filter(
          f =>
            f.type === "application/zip" ||
            f.name.toLowerCase().endsWith(".zip")
        );
        const csvFiles = files.filter(
          f => f.type === "text/csv" || f.name.toLowerCase().endsWith(".csv")
        );
        const restFiles = files.filter(
          f =>
            !(
              f.type === "application/zip" ||
              f.name.toLowerCase().endsWith(".zip") ||
              f.type === "text/csv" ||
              f.name.toLowerCase().endsWith(".csv")
            )
        );

        for (const csvFile of csvFiles) {
          const { filename, uploadUrl } = await uploadFile(csvFile);
          const jsonMapping = await pimMappingProcessRef.current?.start({
            filename,
            url: uploadUrl,
          });
          if (jsonMapping) {
            await addPimImport({
              variables: {
                input: {
                  uploadUrl,
                  filename,
                  supplierId,
                  configuration: JSON.stringify({
                    separator: ";",
                    decimal_symbol: ",",
                    columns_mapping: jsonMapping,
                  }),
                },
              },
            });
          }
        }

        for (const zipFile of zipFiles) {
          const { filename, uploadUrl } = await uploadFile(zipFile);
          await addPimImport({
            variables: {
              input: {
                uploadUrl,
                filename,
                supplierId,
                configuration: null,
              },
            },
          });
        }

        if (restFiles.length > 0) {
          const zip = await import("@zip.js/zip.js");
          const zipWriter = new zip.ZipWriter(
            new zip.BlobWriter("application/zip"),
            { useWebWorkers: true, bufferedWrite: true }
          );
          for (const file of restFiles) {
            await zipWriter.add(file.name, new zip.BlobReader(file));
          }

          const zipBlob = await zipWriter.close();

          const zipFile = new File(
            [zipBlob],
            `catalog_${moment().format("YYYY-MM-DD_HH-mm-ss")}.zip`,
            { type: "application/zip" }
          );

          const { filename, uploadUrl } = await uploadFile(zipFile);

          await addPimImport({
            variables: {
              input: {
                uploadUrl,
                filename,
                supplierId,
                configuration: null,
              },
            },
          });
        }

        // When the caller provides `refetchQueries`, the mutation already
        // refetches those; only fall back to a global refetch otherwise to
        // avoid redundantly re-running the same (and unrelated) active queries.
        if (!refetchQueries) {
          await client.reFetchObservableQueries();
        }

        setStatus("idle");
      } catch (e) {
        // Surface a typed failure to the user; keep the raw error for diagnosis.
        setStatus("error");
        console.error(e);
        Sentry.captureException(e);
      }
    },
    [
      client,
      supplierId,
      pimRequestImportationUploadUrlMutation,
      addPimImport,
      refetchQueries,
    ]
  );

  const buttonSize = appearance === "header" ? "small" : "medium";
  const buttonVariant = appearance === "cta" ? "text" : "outlined";

  return (
    <>
      {status === "error" ? (
        <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
          <WarningAmberIcon color="warning" fontSize="small" />
          <Typography variant="body2" color="warning.main">
            {t("Upload failed. Please try again.", {
              ns: "PimImportations",
            })}
          </Typography>
          <Button
            color="warning"
            variant={buttonVariant}
            size={buttonSize}
            onClick={() => {
              if (lastFilesRef.current) {
                void uploadFiles(lastFilesRef.current);
              }
            }}
          >
            {t("Upload again", { ns: "PimImportations" })}
          </Button>
          {/* Let the user pick different files, not just retry the same ones. */}
          <Button
            color="warning"
            variant="text"
            size={buttonSize}
            onClick={() => fileInputRef.current?.click()}
          >
            {t("Upload file", { ns: "PimImportations" })}
          </Button>
        </Stack>
      ) : (
        <Button
          color="primary"
          variant={buttonVariant}
          size={buttonSize}
          startIcon={<AddIcon />}
          loading={status === "loading"}
          loadingPosition="start"
          onClick={() => {
            fileInputRef.current?.click();
          }}
        >
          {t("Upload file", { ns: "PimImportations" })}
        </Button>
      )}
      <FileInput
        innerRef={fileInputRef}
        accept={"*"}
        multiple={true}
        onStart={() => {
          setStatus("loading");
        }}
        onCancel={() => {
          setStatus("idle");
        }}
        onComplete={uploadFiles}
      />
      <PimCsvColumnMappingProcess
        ref={pimMappingProcessRef}
        supplierId={supplierId}
      />
    </>
  );
};
